import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Loader2, Save, Calculator } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const formatVND = (n) => `${(n || 0).toLocaleString("vi-VN")}đ`;
const ALL_COURTS = [1, 2, 3, 4, 5, 6, 7, 8];
const TODAY = new Date().toISOString().slice(0, 10);

const blankSession = () => ({
  date: TODAY, start_time: "08:00", end_time: "18:00", court_ids: [1, 2, 3, 4, 5, 6, 7, 8], note: "",
});

export default function QuoteEditor({ open, onOpenChange, initial, onSaved }) {
  const [customer, setCustomer] = useState({ name: "", phone: "", company: "", email: "", event_name: "", note: "" });
  const [sessions, setSessions] = useState([blankSession()]);
  const [overrideDiscount, setOverrideDiscount] = useState("");
  const [depositPct, setDepositPct] = useState(30);
  const [calc, setCalc] = useState(null);
  const [calcing, setCalcing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (initial) {
        setCustomer({
          name: initial.customer_name || "", phone: initial.customer_phone || "",
          company: initial.customer_company || "", email: initial.customer_email || "",
          event_name: initial.event_name || "", note: initial.note || "",
        });
        setSessions(initial.input_sessions?.length ? initial.input_sessions : [blankSession()]);
        setOverrideDiscount(initial.override_discount_pct ?? "");
        setDepositPct(initial.deposit_pct ?? 30);
        setCalc(initial.calc);
      } else {
        setCustomer({ name: "", phone: "", company: "", email: "", event_name: "", note: "" });
        setSessions([blankSession()]);
        setOverrideDiscount(""); setDepositPct(30); setCalc(null);
      }
    }
  }, [open, initial]);

  const buildPayload = () => ({
    customer_name: customer.name, customer_phone: customer.phone, customer_company: customer.company,
    customer_email: customer.email, event_name: customer.event_name, note: customer.note,
    sessions: sessions.map(s => ({
      date: s.date, start_time: s.start_time, end_time: s.end_time,
      court_ids: s.court_ids, note: s.note || "",
    })),
    override_discount_pct: overrideDiscount === "" ? null : Number(overrideDiscount),
    deposit_pct: Number(depositPct),
  });

  const handleCalc = async () => {
    if (!customer.name.trim()) { toast.warning("Nhập tên khách hàng để tính"); return; }
    if (sessions.some(s => !s.date || !s.court_ids?.length)) { toast.warning("Vui lòng nhập đủ data các phiên"); return; }
    setCalcing(true);
    try {
      const { data } = await api.post("/quotes/calculate", buildPayload());
      setCalc(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Lỗi tính giá");
    } finally { setCalcing(false); }
  };

  const handleSave = async () => {
    if (!customer.name.trim()) { toast.warning("Nhập tên khách hàng"); return; }
    setSaving(true);
    try {
      if (initial?.id) {
        await api.put(`/quotes/${initial.id}`, buildPayload());
        toast.success(`Đã cập nhật ${initial.code}`);
      } else {
        const { data } = await api.post("/quotes", buildPayload());
        toast.success(`Đã tạo ${data.code}`);
      }
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Lỗi lưu báo giá");
    } finally { setSaving(false); }
  };

  const toggleCourt = (idx, cid) => {
    setSessions(prev => prev.map((s, i) => i !== idx ? s : ({
      ...s,
      court_ids: s.court_ids.includes(cid) ? s.court_ids.filter(x => x !== cid) : [...s.court_ids, cid].sort((a, b) => a - b),
    })));
  };

  const updateSession = (idx, field, value) => {
    setSessions(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };

  const removeSession = (idx) => setSessions(prev => prev.filter((_, i) => i !== idx));
  const addSession = () => setSessions(prev => [...prev, blankSession()]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f172a] border-[#334155] max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="quote-editor-dialog">
        <DialogHeader>
          <DialogTitle className="text-slate-50">{initial ? `Sửa ${initial.code}` : "Tạo báo giá mới"}</DialogTitle>
          <DialogDescription className="text-slate-400 text-sm">Nhập thông tin khách hàng + các phiên thuê sân. Hệ thống tự tính giá + chiết khấu.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Khách hàng */}
          <section className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-slate-300 text-xs">Tên khách hàng *</Label>
              <Input value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })}
                placeholder="CLB Cầu lông HN"
                className="bg-[#1e293b] border-[#334155] text-slate-50" data-testid="quote-customer-name" />
            </div>
            <div className="space-y-1">
              <Label className="text-slate-300 text-xs">Tên giải / sự kiện</Label>
              <Input value={customer.event_name} onChange={(e) => setCustomer({ ...customer, event_name: e.target.value })}
                placeholder="Giải mở rộng KARINOX 2026"
                className="bg-[#1e293b] border-[#334155] text-slate-50" data-testid="quote-event-name" />
            </div>
            <div className="space-y-1">
              <Label className="text-slate-300 text-xs">SDT</Label>
              <Input value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })}
                placeholder="0901234567"
                className="bg-[#1e293b] border-[#334155] text-slate-50 font-mono" data-testid="quote-customer-phone" />
            </div>
            <div className="space-y-1">
              <Label className="text-slate-300 text-xs">Email</Label>
              <Input type="email" value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })}
                placeholder="contact@club.vn" className="bg-[#1e293b] border-[#334155] text-slate-50" />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label className="text-slate-300 text-xs">Công ty / tổ chức</Label>
              <Input value={customer.company} onChange={(e) => setCustomer({ ...customer, company: e.target.value })}
                placeholder="Công ty TNHH Thể thao XYZ" className="bg-[#1e293b] border-[#334155] text-slate-50" />
            </div>
          </section>

          {/* Sessions */}
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-lime-400">Các phiên thuê sân</h3>
              <Button onClick={addSession} size="sm" variant="outline"
                className="border-lime-500/40 text-lime-300 hover:bg-lime-500/10" data-testid="quote-add-session">
                <Plus className="w-3 h-3 mr-1" /> Thêm phiên
              </Button>
            </div>

            {sessions.map((s, idx) => (
              <div key={idx} className="bg-[#1e293b] border border-[#334155] rounded-lg p-3 space-y-3" data-testid={`quote-session-${idx}`}>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-400 font-semibold">PHIÊN #{idx + 1}</span>
                  {sessions.length > 1 && (
                    <button onClick={() => removeSession(idx)}
                      className="text-red-400 hover:text-red-300" data-testid={`quote-remove-session-${idx}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <div className="space-y-1 col-span-2 sm:col-span-1">
                    <Label className="text-slate-400 text-[10px]">Ngày</Label>
                    <Input type="date" value={s.date} onChange={(e) => updateSession(idx, "date", e.target.value)}
                      className="bg-[#0f172a] border-[#334155] text-slate-50 text-sm w-full" data-testid={`quote-date-${idx}`} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-slate-400 text-[10px]">Bắt đầu</Label>
                    <Input type="time" value={s.start_time} onChange={(e) => updateSession(idx, "start_time", e.target.value)}
                      className="bg-[#0f172a] border-[#334155] text-slate-50 text-sm w-full" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-slate-400 text-[10px]">Kết thúc</Label>
                    <Input type="time" value={s.end_time} onChange={(e) => updateSession(idx, "end_time", e.target.value)}
                      className="bg-[#0f172a] border-[#334155] text-slate-50 text-sm w-full" />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-slate-400 text-[10px]">Chọn sân ({s.court_ids.length} sân)</Label>
                  <div className="flex gap-1.5 flex-wrap">
                    {ALL_COURTS.map(cid => {
                      const selected = s.court_ids.includes(cid);
                      const group = cid <= 3 ? "1-3" : cid <= 6 ? "4-6" : "7-8";
                      const groupColor = cid <= 3 ? "amber" : cid <= 6 ? "cyan" : "purple";
                      return (
                        <button key={cid} onClick={() => toggleCourt(idx, cid)}
                          title={`Sân ${cid} (nhóm ${group})`}
                          className={`w-9 h-9 rounded text-xs font-bold border transition-colors ${
                            selected
                              ? `bg-${groupColor}-500 border-${groupColor}-400 text-[#0f172a]`
                              : `bg-[#0f172a] border-[#334155] text-slate-500 hover:border-${groupColor}-500/50`
                          }`}
                          data-testid={`quote-court-${idx}-${cid}`}>
                          {cid}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[10px] text-slate-500">🟡 Nhóm 1-3 · 🔵 Nhóm 4-6 · 🟣 Nhóm 7-8 (giá khác nhau)</p>
                </div>
              </div>
            ))}
          </section>

          {/* Override discount + deposit */}
          <section className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-slate-300 text-xs">Override chiết khấu % (để trống = auto)</Label>
              <Input type="number" min="0" max="100" value={overrideDiscount}
                onChange={(e) => setOverrideDiscount(e.target.value)}
                placeholder="Auto"
                className="bg-[#1e293b] border-[#334155] text-slate-50" data-testid="quote-override-discount" />
            </div>
            <div className="space-y-1">
              <Label className="text-slate-300 text-xs">Tạm ứng % (giữ chỗ)</Label>
              <Input type="number" min="0" max="100" value={depositPct}
                onChange={(e) => setDepositPct(e.target.value)}
                className="bg-[#1e293b] border-[#334155] text-slate-50" data-testid="quote-deposit-pct" />
            </div>
            <div className="space-y-1 col-span-2">
              <Label className="text-slate-300 text-xs">Ghi chú (in trong báo giá)</Label>
              <Input value={customer.note} onChange={(e) => setCustomer({ ...customer, note: e.target.value })}
                placeholder="Vd: Khách VIP, cần phòng thay đồ thêm..."
                className="bg-[#1e293b] border-[#334155] text-slate-50" />
            </div>
          </section>

          {/* Calc preview */}
          <section>
            <Button onClick={handleCalc} disabled={calcing} variant="outline"
              className="w-full border-lime-500/40 text-lime-300 hover:bg-lime-500/10 gap-2" data-testid="quote-calc-btn">
              {calcing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calculator className="w-4 h-4" />}
              Tính thử (preview)
            </Button>
            {calc && (
              <div className="mt-3 bg-gradient-to-br from-lime-500/10 to-emerald-500/5 border border-lime-500/30 rounded-lg p-4 space-y-2" data-testid="quote-calc-result">
                <div className="flex justify-between text-sm"><span className="text-slate-400">Tổng giá gốc ({calc.sessions.length} phiên)</span>
                  <span className="text-slate-100 font-mono">{formatVND(calc.total_gross)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-amber-400">
                  Chiết khấu {calc.discount_pct}% {calc.discount_pct === calc.auto_discount_pct ? "(auto)" : "(manual)"}
                </span>
                  <span className="text-amber-400 font-mono">− {formatVND(calc.discount_amount)}</span></div>
                <div className="flex justify-between text-lg font-bold pt-2 border-t border-lime-500/20"><span className="text-slate-50">Thành tiền</span>
                  <span className="text-lime-400 font-mono">{formatVND(calc.total_net)}</span></div>
                <div className="flex justify-between text-xs opacity-70"><span className="text-slate-500">Tạm ứng {calc.deposit_pct}%</span>
                  <span className="text-slate-300 font-mono">{formatVND(calc.deposit_amount)}</span></div>
              </div>
            )}
          </section>

          {/* Actions */}
          <div className="flex gap-2 pt-3 border-t border-[#334155]">
            <Button onClick={() => onOpenChange(false)} variant="outline" className="flex-1 border-[#334155] text-slate-300">Hủy</Button>
            <Button onClick={handleSave} disabled={saving}
              className="flex-1 bg-lime-500 hover:bg-lime-400 text-[#0f172a] font-semibold gap-2" data-testid="quote-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {initial ? "Cập nhật" : "Tạo báo giá"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
